import { useContext } from 'react';
import { Row, Col, UncontrolledTooltip } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { TextInput, Select } from '@octano/global-ui';
import { useValidations } from '../../../../../hooks/useValidations';

import { CourseFormContext } from '../../providers/CourseProvider';
import { SectionTitle } from '../../../../../components/text';
import { CourseFormFields } from '../../types';
import { TranslationsKeys } from '../../../../../locales/translations';

type CourseDetailsSectionProps = {
  readOnly?: boolean;
};

/**
 * HU 15624 — Sección "Detalles principales" del tab Administrativo.
 *
 * Agrupa los campos OBLIGATORIOS de la asignatura (FRD §1.3.a):
 *   - Datos básicos: sigla, código, nombre, escuela, modalidad, nivel, créditos.
 *   - Subsección "Configuración adicional": Educación Continua, Habilitación,
 *     Supletorios.
 *
 * Los campos de horas (totalHours / presentialPedagogicalHours / weeklyHours)
 * se trasladaron a `CourseOptionalDetailsSection` (sección "Detalles
 * opcionales").
 */
const CourseDetailsSection = ({
  readOnly = false,
}: CourseDetailsSectionProps) => {
  const { t } = useTranslation(TranslationsKeys.ACADEMIC_OFFER_COURSES_UPDATE);
  const { msgValidations, rulesPatternNumeric } = useValidations();

  const { control } = useFormContext<CourseFormFields>();

  const { isDisabledToUpdate, formData } = useContext(CourseFormContext);
  const {
    schools = [],
    academicLevels = [],
    modalities = [],
    replaceCourseGradeOptions = [],
    coursesFeatures,
  } = formData;

  const hasAdditionalConfig =
    !!coursesFeatures?.replaceCourseGradeAllowed ||
    !!coursesFeatures?.replaceEvaluationGradeAllowed;

  return (
    <>
      <Row className="pt-4">
        <Col xs={6} lg={4}>
          <TextInput
            name="shortening"
            label={t(`shortening`)}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={6} lg={4}>
          <TextInput
            name="code"
            label={t(`code`)}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <TextInput
            name="name"
            label={t(`name`)}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="school"
            label={t(`school`)}
            options={schools}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="modality"
            label={t(`modality`)}
            options={modalities}
            control={control}
            isClearable={true}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <Select
            name="academicLevel"
            label={t(`academicLevel`)}
            options={academicLevels}
            control={control}
            rules={{ required: msgValidations.required }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
        <Col xs={12} lg={4}>
          <TextInput
            name="credits"
            label={t(`credits`)}
            control={control}
            rules={{
              required: msgValidations.required,
              pattern: rulesPatternNumeric,
            }}
            disabled={readOnly || isDisabledToUpdate}
          />
        </Col>
      </Row>
      <Row>
        <Col xs={12} className="pt-4 pb-3">
          <SectionTitle text={t(`additionalConfigSubsection`)} />
        </Col>
        {hasAdditionalConfig && !!coursesFeatures?.replaceCourseGradeAllowed && (
          <Col xs={12} lg={4}>
            <Select
              name="replaceCourseGradeAllowed"
              label={t(`replaceCourseGradeAllowed`)}
              options={replaceCourseGradeOptions}
              control={control}
              disabled={readOnly}
            />
          </Col>
        )}
        {hasAdditionalConfig &&
          !!coursesFeatures?.replaceEvaluationGradeAllowed && (
            <Col xs={12} lg={4}>
              <Select
                name="replaceEvaluationGradeAllowed"
                label={t(`replaceEvaluationGradeAllowed`)}
                control={control}
                options={replaceCourseGradeOptions}
                disabled={readOnly}
              />
            </Col>
          )}
        <Col xs={12} lg={4}>
          <div id="isContinuousEducationAvailableContainer">
            <Select
              name="isContinuousEducationAvailable"
              label={t(
                'isContinuousEducationAvailable',
                'Disponibilidad para educación continua',
              )}
              control={control}
              options={[
                { label: 'Si', value: 1 },
                { label: 'No', value: 0 },
              ]}
              rules={{ required: msgValidations.required }}
              disabled={readOnly}
            />
          </div>
          <UncontrolledTooltip target="isContinuousEducationAvailableContainer">
            {t(
              'isContinuousEducationAvailableTooltip',
              'En caso de marcar "Si", la asignatura podra ofertarse como curso o programa de educación continua.',
            )}
          </UncontrolledTooltip>
        </Col>
      </Row>
    </>
  );
};

export default CourseDetailsSection;
